import { Client, xml } from "@xmpp/client"
import { Element } from "@xmpp/xml"
import iqMeasure from "../instrumentation/iqMeasure"
import AccountChatsParser, {
  ACCOUNT_CHAT_NS,
  USER_CHAT_NS,
} from "../lib/accountChatsParser"
import SettingsParser from "../lib/settingsParser"
import { CarRouteConfig, RouteAlertConfig } from "./adHocCommands"

const pubsubGetIq = (
  client: Client,
  pubJid: string,
  node: string,
  ids?: string[],
) => {
  const items = xml("items", {
    node: node,
  })
  if (ids) {
    ids.forEach((id) => {
      items.append(xml("item", { id }))
    })
  }

  return xml(
    "iq",
    { from: client.jid, type: "get", to: pubJid },
    xml(
      "pubsub",
      {
        xmlns: "http://jabber.org/protocol/pubsub",
      },
      items,
    ),
  )
}

const XmppPubSubApi = {
  discover: (client: Client, pubJid: string) => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller, "pubsub_disco_items")

    const discoStanza = xml(
      "iq",
      { from: client.jid, type: "get", to: pubJid },
      xml("query", "http://jabber.org/protocol/disco#items"),
    )
    measuredIqCaller.request(discoStanza)
  },

  discoverItems: (client: Client, pubJid: string) => {
    const { iqCaller } = client
    const measuredIqCaller = iqMeasure(iqCaller, "pubsub_disco_items_geoloc")

    const discoStanza = xml(
      "iq",
      { from: client.jid, type: "get", to: pubJid },
      xml("query", {
        xmlns: "http://jabber.org/protocol/disco#items",
        node: "http://jabber.org/protocol/geoloc",
      }),
    )
    measuredIqCaller.request(discoStanza)
  },

  checkNodeExists: async (
    client: Client,
    pubJid: string,
    node: string,
  ): Promise<boolean> => {
    const { iqCaller } = client

    // Request to discover all items (nodes)
    const discoItemsStanza = xml(
      "iq",
      { from: client.jid, type: "get", to: pubJid },
      xml("query", {
        xmlns: "http://jabber.org/protocol/disco#items",
      }),
    )

    try {
      const result = await iqCaller.request(discoItemsStanza)

      // Extract nodes from the response
      const items = result
        .getChildren("query")
        .flatMap((query) => query.getChildren("item"))
        .map((item) => item.attrs.node)

      // Check if the specified node exists in the list
      return items.includes(node)
    } catch (error) {
      console.error("Error fetching nodes:", error)
      return false
    }
  },

  fetchAccountChats: async (client: Client) => {
    const { iqCaller } = client
    const stanza = pubsubGetIq(
      client,
      `wizard@${client.jid!.domain}`,
      ACCOUNT_CHAT_NS,
    )

    const result = await iqCaller.request(stanza)
    return AccountChatsParser.parse(result)
  },
  fetchUserChats: async (client: Client) => {
    const { iqCaller } = client
    const stanza = pubsubGetIq(
      client,
      `wizard@${client.jid!.domain}`,
      USER_CHAT_NS,
    )

    try {
      const result = await iqCaller.request(stanza)
      return AccountChatsParser.parse(result, {
        configElementName: "user-chat-config",
        loggedInUserJidBare: client.jid?.bare().toString(),
      })
    } catch (error: any) {
      if (error?.condition === "item-not-found") {
        return []
      }
      throw error
    }
  },

  subscribe: (
    client: Client,
    pubJid: string,
    node: string,
  ): Promise<Element> => {
    const { iqCaller } = client

    const subscribeStanza = xml(
      "iq",
      { from: client.jid, type: "set", to: pubJid },
      xml(
        "pubsub",
        "http://jabber.org/protocol/pubsub",
        xml("subscribe", {
          node,
          jid: client.jid?.bare(),
        }),
      ),
    )
    return iqCaller.request(subscribeStanza)
  },
  fetchCarConfig: async (
    client: Client,
    driverJid: string,
  ): Promise<CarRouteConfig | undefined> => {
    const { iqCaller } = client
    const stanza = pubsubGetIq(
      client,
      `wizard@${client.jid!.domain}`,
      "urn:slickshift:account:car-route-config:0",
      [driverJid],
    )

    const result = await iqCaller.request(stanza)
    if (!result) return undefined

    return SettingsParser.parseCarRouteConfig(result)
  },
  fetchRouteAlertConfig: async (
    client: Client,
    driverJid: string,
  ): Promise<RouteAlertConfig | undefined> => {
    const { iqCaller } = client
    const stanza = pubsubGetIq(
      client,
      `wizard@${client.jid!.domain}`,
      "urn:slickshift:account:route-alert-config:0",
      [driverJid],
    )

    const result = await iqCaller.request(stanza)
    if (!result) return undefined

    return SettingsParser.parseRouteAlertConfig(result)
  },
}
export default XmppPubSubApi
